import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const TIERS = {
  starter: {
    name: 'Starter',
    monthlyPrice: 69,
    setupFee: 0,
    features: [
      '25 active loans',
      '50 property listings',
      'All states included',
      'Customer portal',
      'Email support',
      'Basic reporting'
    ],
    color: '#3b82f6',
    popular: false
  },
  pro: {
    name: 'PRO',
    monthlyPrice: 99,
    setupFee: 299,
    features: [
      '100 active loans',
      '200 property listings',
      'All states included',
      'Priority support',
      'Custom branding',
      'Advanced reports',
      'eBay integration',
      'Tax escrow'
    ],
    color: '#8b5cf6',
    popular: true
  },
  enterprise: {
    name: 'Enterprise',
    monthlyPrice: 199,
    setupFee: 999,
    features: [
      'Unlimited loans',
      'Unlimited listings',
      'All states included',
      'Dedicated support',
      'White-label branding',
      'API access',
      'Custom integrations',
      'Priority onboarding',
      'Custom domain'
    ],
    color: '#f59e0b',
    popular: false
  }
};

function TierSelection() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in and get current tier
    const checkAuth = async () => {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/billing/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();

          // If already on a paid plan, redirect to dashboard
          if (data.tier && data.tier !== 'free_trial' && data.status === 'active') {
            navigate('/admin/dashboard');
            return;
          }
        }
      } catch (err) {
        console.error('Failed to check billing status:', err);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  const handleSelectTier = (tierKey) => {
    // Store selected tier and navigate to payment
    sessionStorage.setItem('selectedTier', tierKey);
    navigate('/signup/payment');
  };

  const handleStartFreeTrial = () => {
    // Go directly to dashboard with free trial
    navigate('/admin/dashboard');
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, var(--color-background-alt) 0%, var(--color-border) 100%)'
      }}>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, var(--color-background-alt) 0%, var(--color-border) 100%)',
      padding: '40px 20px'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '50px' }}>
          <h1 style={{
            fontSize: '36px',
            fontWeight: '700',
            color: 'var(--color-text)',
            marginBottom: '15px'
          }}>
            Choose Your Plan
          </h1>
          <p style={{
            fontSize: '18px',
            color: 'var(--color-text-light)',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            Select the plan that best fits your business needs. Upgrade or downgrade anytime.
          </p>
        </div>

        {/* Tier Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '30px',
          marginBottom: '40px'
        }}>
          {Object.entries(TIERS).map(([tierKey, tier]) => (
            <div
              key={tierKey}
              style={{
                background: 'var(--color-surface)',
                borderRadius: '20px',
                padding: '35px',
                position: 'relative',
                boxShadow: tier.popular
                  ? '0 20px 40px rgba(139, 92, 246, 0.2)'
                  : '0 10px 30px rgba(0,0,0,0.08)',
                border: tier.popular ? `3px solid ${tier.color}` : '1px solid #eee',
                transform: tier.popular ? 'scale(1.05)' : 'scale(1)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {tier.popular && (
                <div style={{
                  position: 'absolute',
                  top: '-15px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: tier.color,
                  color: 'white',
                  padding: '8px 25px',
                  borderRadius: '25px',
                  fontSize: '13px',
                  fontWeight: '700',
                  letterSpacing: '0.5px'
                }}>
                  MOST POPULAR
                </div>
              )}

              <h2 style={{
                fontSize: '24px',
                fontWeight: '700',
                color: tier.color,
                marginBottom: '10px',
                marginTop: tier.popular ? '10px' : '0'
              }}>
                {tier.name}
              </h2>

              <div style={{ marginBottom: '25px' }}>
                <span style={{
                  fontSize: '48px',
                  fontWeight: '800',
                  color: 'var(--color-text)'
                }}>
                  ${tier.monthlyPrice}
                </span>
                <span style={{ color: 'var(--color-text-light)', fontSize: '18px' }}>/month</span>
              </div>

              {tier.setupFee > 0 && (
                <p style={{
                  color: 'var(--color-text-muted)',
                  fontSize: '14px',
                  marginBottom: '25px',
                  paddingBottom: '25px',
                  borderBottom: '1px solid var(--color-border)'
                }}>
                  + ${tier.setupFee} one-time setup fee
                </p>
              )}

              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: '0 0 30px 0'
              }}>
                {tier.features.map((feature, i) => (
                  <li key={i} style={{
                    padding: '10px 0',
                    fontSize: '15px',
                    color: 'var(--color-text)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <span style={{
                      color: tier.color,
                      fontSize: '18px',
                      fontWeight: 'bold'
                    }}>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSelectTier(tierKey)}
                style={{
                  width: '100%',
                  padding: '16px',
                  borderRadius: '12px',
                  border: 'none',
                  background: tier.popular
                    ? `linear-gradient(135deg, ${tier.color}, ${tier.color}dd)`
                    : '#f0f0f0',
                  color: tier.popular ? 'white' : '#333',
                  fontSize: '16px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginTop: 'auto'
                }}
                onMouseOver={(e) => {
                  if (!tier.popular) {
                    e.target.style.background = tier.color;
                    e.target.style.color = 'white';
                  }
                }}
                onMouseOut={(e) => {
                  if (!tier.popular) {
                    e.target.style.background = '#f0f0f0';
                    e.target.style.color = '#333';
                  }
                }}
              >
                Select {tier.name}
              </button>
            </div>
          ))}
        </div>

        {/* Free Trial Option */}
        <div style={{
          textAlign: 'center',
          padding: '30px',
          background: 'var(--color-surface)',
          borderRadius: '15px',
          boxShadow: '0 5px 20px rgba(0,0,0,0.05)'
        }}>
          <h3 style={{
            fontSize: '20px',
            color: 'var(--color-text)',
            marginBottom: '10px'
          }}>
            Not ready to commit?
          </h3>
          <p style={{
            color: 'var(--color-text-light)',
            marginBottom: '20px'
          }}>
            Start with a 14-day free trial. No credit card required.
          </p>
          <button
            onClick={handleStartFreeTrial}
            style={{
              padding: '14px 40px',
              borderRadius: '10px',
              border: '2px solid var(--color-border)',
              background: 'transparent',
              color: 'var(--color-text-light)',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.borderColor = 'var(--color-primary)';
              e.target.style.color = 'var(--color-primary)';
            }}
            onMouseOut={(e) => {
              e.target.style.borderColor = '#ddd';
              e.target.style.color = '#666';
            }}
          >
            Start Free Trial Instead
          </button>
          <p style={{
            fontSize: '13px',
            color: 'var(--color-text-muted)',
            marginTop: '15px'
          }}>
            Free trial includes: 3 active loans, 10 listings, 1 state, basic features
          </p>
        </div>

        {/* Footer */}
        <div style={{
          textAlign: 'center',
          marginTop: '40px',
          color: 'var(--color-text-muted)',
          fontSize: '14px'
        }}>
          <p>All plans include secure payment processing and customer support.</p>
          <p style={{ marginTop: '10px' }}>
            Questions?{' '}
            <a href="https://deedstack.com/contact" style={{ color: 'var(--color-brand-text)' }}>
              Contact us
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default TierSelection;
